import { ExternalLink, FileText, FileX2 } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";

import { nodeTitle } from "@/features/caseFiles/nodeNames";
import type { FsNode } from "../caseFiles/types";
import { REMOVED_EXHIBIT_TEXT, pinciteText } from "./model";
import { usePagePreview } from "./usePagePreview";

/** Letter-page aspect keeps the card from jumping while the PNG loads. */
const PREVIEW_CLASS =
  "relative aspect-[8.5/11] w-full overflow-hidden rounded-md border border-border bg-fill-1";

const PagePreview: FC<{
  individualId: string;
  nodeId: string;
  page: number | null;
  version?: string | null;
  alt: string;
}> = ({ individualId, nodeId, page, version, alt }) => {
  const preview = usePagePreview(individualId, nodeId, page, version);
  if (preview.status === "unavailable") {
    return (
      <div
        className={cn(PREVIEW_CLASS, "flex items-center justify-center")}
        data-testid="citation-preview-unavailable"
      >
        <FileText className="size-8 text-fg-4" />
      </div>
    );
  }
  return (
    <div className={PREVIEW_CLASS}>
      {preview.status === "ready" ? (
        <img
          src={preview.url}
          alt={alt}
          className="absolute inset-0 h-full w-full object-contain object-top"
          data-testid="citation-preview"
        />
      ) : (
        <div
          className="absolute inset-0 animate-pulse bg-muted"
          aria-busy="true"
          data-testid="citation-preview-loading"
        />
      )}
    </div>
  );
};

export type CitationCardBodyProps = {
  individualId: string;
  page: number | null;
  /** The cited file, or null when it is no longer in the case. */
  node: FsNode | null;
  /** Live label ("Exhibit B.2 at 3"), or null when the exhibit has no label. */
  label: string | null;
  /** "Open exhibit": show the file in the case IDE at the pinned page. */
  onOpen?: (node: FsNode, page: number | null) => void;
};

/**
 * The inside of a citation card: the exhibit's current label, the document,
 * a preview of the cited page, and a way to open it there. Shared by the
 * editor's hover card and the chips in chat.
 */
export const CitationCardBody: FC<CitationCardBodyProps> = ({
  individualId,
  page,
  node,
  label,
  onOpen
}) => {
  const removed = !node || !label;
  const pincite = pinciteText(page);
  return (
    <>
      <div className="space-y-0.5">
        {removed ? (
          <div className="flex items-center gap-1.5 text-warn">
            <FileX2 className="size-3.5 shrink-0" />
            <span className="text-xs font-medium">{REMOVED_EXHIBIT_TEXT}</span>
          </div>
        ) : (
          <div className="text-sm font-semibold text-foreground">{label}</div>
        )}
        {node ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="truncate" title={nodeTitle(node)}>
              {nodeTitle(node)}
            </span>
            {node.page_count ? (
              <span className="ml-auto shrink-0 tabular-nums">
                {pincite
                  ? `${pincite} of ${node.page_count}`
                  : `${node.page_count} pages`}
              </span>
            ) : pincite ? (
              <span className="ml-auto shrink-0 tabular-nums">{pincite}</span>
            ) : null}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            This citation points at a file that is no longer in the case.
          </div>
        )}
      </div>
      {node ? (
        <PagePreview
          individualId={individualId}
          nodeId={node.id}
          page={page}
          version={node.checksum ?? null}
          alt={`${nodeTitle(node)}${pincite ? `, ${pincite}` : ""}`}
        />
      ) : null}
      {node && onOpen ? (
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-full gap-1.5 text-xs"
          onClick={() => onOpen(node, page)}
          data-testid="citation-open"
        >
          <ExternalLink className="size-3.5" />
          {page ? `Open at page ${page}` : "Open exhibit"}
        </Button>
      ) : null}
    </>
  );
};

export type CitationHoverCardProps = CitationCardBodyProps & {
  /** Where to anchor, or null to close. */
  anchor: DOMRect | null;
  nodeId: string | null;
  /** Keep the card open while the pointer is over it. */
  onHold?: (on: boolean) => void;
  onDismiss?: () => void;
  className?: string;
};

/**
 * The card over a citation in the docx editor. Controlled: the host decides
 * when it is open and where it sits (a virtual anchor at the hovered text),
 * because the editor's painted chrome is pointer-transparent and cannot be a
 * Radix trigger itself.
 */
export const CitationHoverCard: FC<CitationHoverCardProps> = ({
  anchor,
  nodeId,
  onHold,
  onDismiss,
  className,
  ...body
}) => {
  const open = Boolean(anchor && nodeId);

  return (
    <HoverCard
      open={open}
      openDelay={0}
      closeDelay={120}
      onOpenChange={(next) => {
        if (!next) onDismiss?.();
      }}
    >
      <HoverCardTrigger asChild>
        <span
          aria-hidden="true"
          data-testid="citation-anchor"
          style={{
            position: "fixed",
            left: anchor?.left ?? 0,
            top: anchor?.top ?? 0,
            width: anchor?.width ?? 0,
            height: anchor?.height ?? 0,
            pointerEvents: "none"
          }}
        />
      </HoverCardTrigger>
      {open && nodeId ? (
        <HoverCardContent
          side="top"
          align="start"
          collisionPadding={8}
          className={cn("w-64 space-y-2 p-2.5", className)}
          data-testid="citation-hover-card"
          onPointerEnter={() => onHold?.(true)}
          onPointerLeave={() => onHold?.(false)}
          onEscapeKeyDown={() => onDismiss?.()}
        >
          <CitationCardBody {...body} />
        </HoverCardContent>
      ) : null}
    </HoverCard>
  );
};
